import { Eye, EyeOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginAdminWithEmail } from "../api/adminAuth";
import OtpVerificationStep from "../component/login/OtpVerificationStep";
import { API_ERROR_TYPE } from "../constants/errorType";
import { useAccess } from "../context/AccessContext";
import { useUser } from "../context/UserContext"; // UserContext 사용
import { getHomePath } from "../utils/routeAccess";
import "../css/Login.css";

// 1단계 로그인 실패 시 서버 메시지(detail)를 그대로 보여주는 오류
const LOGIN_DETAIL_ERROR_TYPES = [
  "/errors/authentication",
  "/errors/not-found",
  API_ERROR_TYPE.ADMIN_LOGIN_INVALID,
  API_ERROR_TYPE.ADMIN_PHONE_NOT_REGISTERED,
  API_ERROR_TYPE.ADMIN_OTP_SEND_LIMIT,
];

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false); // 아이디 저장 상태
  const [showPassword, setShowPassword] = useState(false); // 비밀번호 보기/숨기기 상태
  // SMS 인증 단계 정보 (challengeId 포함) - 보안상 컴포넌트 state에만 보관, 새로고침 시 1단계로 돌아감
  const [otpChallenge, setOtpChallenge] = useState(null);
  const [loginError, setLoginError] = useState(""); // 인증 단계에서 돌아왔을 때 1단계 폼에 표시할 메시지
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const loggingInRef = useRef(false); // 중복 제출 방지 (인증번호 중복 발송 방지)
  const { setUserInfo } = useUser(); // Context에서 setUserInfo 가져오기
  const { refreshAccess } = useAccess();

  const navigate = useNavigate(); // 페이지 이동을 위한 Hook

  // 컴포넌트 마운트 시 저장된 이메일 불러오기
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // 로그인 성공 처리 (1단계 바로 성공 / 2단계 인증 성공 공통)
  const completeLogin = async (userInfo) => {
    setUserInfo(userInfo);

    // 아이디 저장 처리
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", email);
    } else {
      localStorage.removeItem("rememberedEmail");
    }

    // 로그인 직후 권한 조회(R-FE-01) → 권한이 있는 첫 메뉴로 이동(R-FE-05)
    const access = await refreshAccess(userInfo);
    navigate(getHomePath(access.hasPermission), { replace: true });
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!email || !password) {
      alert("이메일과 비밀번호를 입력하세요.");
      return;
    }

    if (loggingInRef.current) return;
    loggingInRef.current = true;
    setIsLoggingIn(true);
    setLoginError("");

    try {
      const data = await loginAdminWithEmail({ email, password });

      // SMS 인증이 필요한 경우 2단계(인증번호 입력)로 전환
      if (data?.otpRequired) {
        setOtpChallenge({
          challengeId: data.challengeId,
          maskedPhone: data.maskedPhone,
          expiresIn: data.expiresIn,
          resendAvailableIn: data.resendAvailableIn,
          smsSent: data.smsSent,
        });
        return;
      }

      await completeLogin(data);
    } catch (error) {
      const errorData = error?.response?.data;
      if (LOGIN_DETAIL_ERROR_TYPES.includes(errorData?.type) && errorData.detail) {
        alert(errorData.detail);
      } else {
        alert("로그인 중 오류가 발생했습니다. 다시 시도해주세요.");
      }
    } finally {
      loggingInRef.current = false;
      setIsLoggingIn(false);
    }
  };

  // 인증 단계 → 1단계로 복귀 (입력한 이메일은 유지)
  const handleRestart = (message) => {
    setOtpChallenge(null);
    setPassword("");
    setLoginError(message || "");
  };

  const togglePasswordVisibility = () => {
    // 비밀번호 보기/숨기기 토글 함수
    setShowPassword((prev) => !prev);
  };

  if (otpChallenge) {
    return <OtpVerificationStep challenge={otpChallenge} onVerified={completeLogin} onRestart={handleRestart} />;
  }

  return (
    <div className="login-box">
      <h2>로그인</h2>
      <form onSubmit={handleLogin}>
        <div className="input-group">
          <label htmlFor="email">이메일</label>
          <div className="input-field-wrapper"> {/* New wrapper for email input */}
            <input
              type="text"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="이메일을 입력하세요"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              inputMode="email"
              spellCheck={false}
            />
          </div>
        </div>
        <div className="input-group">
          <label htmlFor="password">비밀번호</label>
          <div className="input-field-wrapper"> {/* Renamed wrapper for password input */}
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호를 입력하세요"
              autoComplete="current-password"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>
        <div className="remember-me-group">
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          <label htmlFor="rememberMe">아이디 저장</label>
        </div>
        {loginError && (
          <p className="login-inline-error" role="alert">
            {loginError}
          </p>
        )}
        <button type="submit" disabled={isLoggingIn}>
          로그인
        </button>
      </form>
    </div>
  );
}

export default Login;
